import React, { useState } from 'react';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import ChangeRequestList from '@/seller/components/change-requests/ChangeRequestList/ChangeRequestList';
import { searchChangeRequests, SearchChangeRequestsOptions } from '@/services/change-requests';
import { ChangeRequest, BatchedChanges, SellerChangeRequestStatus } from '@/types';
import { useDefaultScrollToId, useAsyncState, useMergeState, useEffectSkipFirst } from '@/utils/hooks';
import { ProjectPermissionToggle } from '@/components/permissions';
import { Trans } from '@lingui/react/macro';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import ChangeRequestMergeDialog from '@/seller/components/change-requests/ChangeRequestMergeDialog';
import { useMappedChangeRequest } from '@/seller/components/change-requests/ChangeRequestFilterableList';
import ChangeRequestCreateDialog from '@/seller/components/change-requests/ChangeRequestCreateDialog';
import { Button, ButtonBar, SlideToggle } from '@ygtechnologies/rubus-ui';

interface ResidentialUnitChangeRequestsProps {
    residentialUnitId: number;
}

function getInitialStatuses() {
    return Object.values(SellerChangeRequestStatus).filter(
        (x) => x !== SellerChangeRequestStatus.Archived && x !== SellerChangeRequestStatus.Closed
    );
}

const ResidentialUnitChangeRequests: React.FC<ResidentialUnitChangeRequestsProps> = ({ residentialUnitId }) => {
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showMergeDialog, setShowMergeDialog] = useState(false);
    const [filters, mergeFilters] = useMergeState<SearchChangeRequestsOptions>({
        units: [residentialUnitId],
        statuses: getInitialStatuses(),
    });

    const [changeRequests, setChangeRequests, reloadChangeRequests] = useAsyncState(
        () => searchChangeRequests(filters),
        [filters],
        null
    );

    useEffectSkipFirst(() => {
        setChangeRequests(null);
        mergeFilters({ units: [residentialUnitId] });
    }, [residentialUnitId, mergeFilters]);

    const defaultExpandedId = useDefaultScrollToId();

    const handleReloadChangeRequests = () => {
        reloadChangeRequests();
    };

    const handleChange = (changes: BatchedChanges<ChangeRequest>) => {
        if (!changeRequests) {
            return;
        }
        setChangeRequests(changes.updateArray(changeRequests));
    };

    const handleCreate = (changeRequest: ChangeRequest) => {
        setChangeRequests([changeRequest, ...changeRequests!]);
    };

    const mappedChangeRequests = useMappedChangeRequest(changeRequests);

    const handleSlideToggle = (e, v: SellerChangeRequestStatus) => {
        const includeClosed = e.target.checked;
        if (includeClosed) {
            mergeFilters({
                statuses: [...filters.statuses!, v],
            });
        } else {
            mergeFilters({
                statuses: filters.statuses!.filter((x) => x !== v),
            });
        }
    };
    return (
        <div>
            <ButtonBar sticky margins="none">
                <ButtonBar.Right>
                    <SlideToggle
                        onChange={(e) => handleSlideToggle(e, SellerChangeRequestStatus.Closed)}
                        checked={filters.statuses?.includes(SellerChangeRequestStatus.Closed) ?? false}>
                        <Trans>Show closed requests</Trans>
                    </SlideToggle>
                    <SlideToggle
                        onChange={(e) => handleSlideToggle(e, SellerChangeRequestStatus.Archived)}
                        checked={filters.statuses?.includes(SellerChangeRequestStatus.Archived) ?? false}>
                        <Trans>Show archived requests</Trans>
                    </SlideToggle>

                    <ProjectPermissionToggle condition="canAssignChangeRequests">
                        <Button variant="tertiary" onClick={(e) => setShowMergeDialog(true)} icon={faPlus}>
                            <Trans>Merge change requests</Trans>
                        </Button>
                        <Button variant="tertiary" onClick={(e) => setShowCreateDialog(true)} icon={faPlus}>
                            <Trans>Create new change request</Trans>
                        </Button>
                    </ProjectPermissionToggle>
                </ButtonBar.Right>
            </ButtonBar>

            {mappedChangeRequests ? (
                <ChangeRequestList
                    defaultExpandedId={defaultExpandedId}
                    changeRequests={mappedChangeRequests}
                    reloadChangeRequests={handleReloadChangeRequests}
                    onChange={handleChange}
                />
            ) : (
                <LoadingIndicator />
            )}

            <ChangeRequestCreateDialog
                residentialUnitId={residentialUnitId}
                show={showCreateDialog}
                onClose={() => setShowCreateDialog(false)}
                onSubmit={handleCreate}
            />

            <ChangeRequestMergeDialog
                // changeRequests={changeRequests || []}
                residentialUnitId={residentialUnitId}
                show={showMergeDialog}
                onClose={() => setShowMergeDialog(false)}
                onSave={() => reloadChangeRequests()}
            />
        </div>
    );
};

export default ResidentialUnitChangeRequests;
